import { Box, Flex, Tooltip, ActionIcon, Skeleton } from "@mantine/core";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import { OverflownText } from "../../../OverflownText";
import {
  GeneratedChosenIcon,
  DuplicateIcon,
  ProductAreaHeaderIcon
} from "../../../../icons/x-symbol-svgrepo-com";
import { IconExternalLink } from "@tabler/icons";
import DuplicateIdeaModal from "../../../ideaNiv/idea-module/DuplicateIdeaModal";
import { useState, useMemo } from "react";

export const TextCell: React.FC<any> = ({ data, col, renderParams }) => {
  const value = getNestedFieldValue(data, col.accessor) || renderParams.default;

  const [duplicateIdeaModalOpened, setDuplicateIdeaModalOpened] = useState(false);

  const addGeneratedIcon = col.accessor === "title" && data.state === "generated";

  // const isStatusCell = col.accessor === "status"

  // const isTitleCell = col.accessor === "title";

  return (
    <Box
      sx={{
        maxWidth: renderParams.maxWidth || "350px",
        width: "100%",
        textOverflow: "ellipsis",
        overflow: "hidden",
        position: "relative",
      }}
      className={`text-cell-${col.accessor.replace(/\./g, '-')}`}
      onClick={(e) => {
        if (duplicateIdeaModalOpened) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Flex align="center">
        {col.accessor === "componentObj.name" && (
          <Box mr={8} pt={5}>
            <ProductAreaHeaderIcon  color="#212529" />
          </Box>
        )}

        {addGeneratedIcon && (
          <Tooltip
            position="top"
            label={"Automatically generated by Bagel AI"}
            withinPortal
          >
            <Box
              w={22}
              pr={3}
              mr={6}
              m={0}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <GeneratedChosenIcon />
            </Box>
          </Tooltip>
        )}

        {data?.duplicateIdea && col.accessor === "title" && (
          <Tooltip
            position="top"
            label={"Duplication Alert"}
            withinPortal
          >
            <Box
              w={22}
              pr={3}
              mr={6}
              m={0}
              sx={{ display: "flex", alignItems: "center" }}
              onClick={(e) => {
                setDuplicateIdeaModalOpened(true);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <DuplicateIcon />
            </Box>
          </Tooltip>
        )}
        <OverflownText
          sx={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            marginLeft: "0px",
            width: "100%",
          }}
          overTooltip={renderParams.tooltip}
          {...renderParams}
        >
          {value}
        </OverflownText>

        {/* duplicate idea modal */}
        {duplicateIdeaModalOpened && <DuplicateIdeaModal
          duplicateIdea={data?.duplicateIdea}
          duplicationType={data?.duplicationType}
          duplicationId={data?.duplicationId}
          verifyOpened={duplicateIdeaModalOpened}
          setVerifyOpened={setDuplicateIdeaModalOpened}
        />}
      </Flex>
      <ActionIcon
        className={`external-link-icon external-link-${col.accessor.replace(/\./g, '-')}`}
        onClick={(e) => {
          e.stopPropagation();
          window.open(`/idea/${data._id}`, "_blank");
        }}
        size="sm"
        sx={{ 
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          right: "0px",
          zIndex: 10,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(2px)",
        }}
      >
        <IconExternalLink size={18} color="#5b60e3" />
      </ActionIcon>
    </Box>
  );
};

// Skeleton component for TextCell
export const TextCellSkeleton: React.FC<{ col: any; id?: string }> = ({ col, id }) => {
  const isTitle = col.accessor === "title";
  const renderParams = col.renderParams || {};
  
  // Calculate width based on column configuration
  const maxWidth = renderParams.maxWidth || col.maxWidth || "350px";
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  
  // Determine skeleton width with memoized random value to prevent changes on re-render
  const skeletonWidth = useMemo(() => {
    if (isTitle) {
      return "350px"; // Fixed width for title
    } else {
      // Generate a deterministic random width based on id (with index) or column accessor
      const seedString = id || col.accessor;
      const hash = seedString.split('').reduce((acc: number, char: string) => {
        return acc + char.charCodeAt(0);
      }, 0);
      const percentage = 40 + (hash % 51); // Results in 40-90%
      return `${percentage}%`;
    }
  }, [isTitle, col.accessor, id]);
  
  return (
    <Box
      sx={{
        maxWidth,
        width: columnWidth || "100%",
        minWidth,
        position: "relative",
        paddingRight: "23px",
      }}
    >
      <Flex align="center">
        <Skeleton height={16} width={skeletonWidth} radius="sm" />
      </Flex>
    </Box>
  );
};
